/* link.tsx */
import AnalyticsInstance from '@shared/utils/analytics';
import ApiUtils from '@shared/utils/api';
import DeviceConfig from '@devices/config';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import Pages from '@shared/types/enums/pages';
import TAccess from '../../templates/access';
import TranslationManager from '@shared/assets/locale/translationsManager';
import User from '@shared/classes/user';
import initAnonymous from '@shared/actions/login/initAnonymous';
import { appSelector } from '@shared/store/slices/app.slice';
import { pushRoute } from '@shared/store/thunks/route.thunk';
import { removeInitialized } from '@shared/store/slices/user.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef } from 'react';
import {
  popRoute,
  selectCurrentParams,
} from '@shared/store/slices/router.slice';

/**
 * Link page
 */
export default function Link() {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(appSelector);
  const currentParams = useAppSelector(selectCurrentParams);
  const paramsRef = useRef(currentParams);

  const { buttonText, description, idDevice, qrUrl, title } = useMemo(() => {
    const buttonText = status.anonymous
      ? TranslationManager.get('PAGES.ACCESS.LINK.BUTTON')
      : null;
    const description = TranslationManager.get(
      'PAGES.ACCESS.LINK.DESCRIPTION',
      ApiUtils.getUrl('activate_link')
    );
    const idDevice = FlavourConfig.capabilities.uuidLink
      ? DeviceConfig.info.uuid
      : undefined;
    const qrUrl = ApiUtils.getUrl('activate_link');
    const title = TranslationManager.get('PAGES.ACCESS.LINK.TITLE');
    return { buttonText, description, idDevice, qrUrl, title };
  }, [status.anonymous]);

  const action = async () => {
    const { app } = store.getState();
    if (app.status.anonymous) {
      initAnonymous(undefined, () => {
        const { userId, operator } = store.getState().user;
        if (!AnalyticsInstance.isInitialized()) return;
        if (userId) {
          AnalyticsInstance.getOmnitrackManager()?.setCustomHeaders({
            'X-Tvup-Userid': userId,
            'X-Tvup-Device': DeviceConfig.info.uuid,
            'X-Tvup-Platform': DeviceConfig.info.platform,
            'X-Tvup-Planid': User.getPlan(null)?.id || '',
          });
        }
        AnalyticsInstance.getOmnitrackManager()?.setLogin(
          userId || '',
          User.getPlan(null)?.code || '',
          operator?.name || ''
        );
      });
      dispatch(setFocus(Focus.MENU));
    }
  };

  const returnAction = () => {
    if (paramsRef.current?.shouldPop) {
      dispatch(popRoute());
      return;
    }
    if (FlavourConfig.languages.length > 1) {
      dispatch(
        pushRoute({ route: { Page: Pages.LANGUAGES, overwrite: true } })
      );
    } else {
      dispatch(pushRoute({ route: { Page: Pages.WELCOME, overwrite: true } }));
    }
  };

  useEffect(() => {
    if (!paramsRef.current?.shouldPop) {
      dispatch(removeInitialized());
    }
  }, []);

  return (
    <TAccess
      action={action}
      buttonText={buttonText}
      description={description}
      idDevice={idDevice}
      qrUrl={qrUrl}
      returnAction={returnAction}
      title={title}
    />
  );
}
